/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import createLoadableComponent from 'js/lib/createLoadableComponent';

import { useVisibilityTracker } from '@coursera/event-pulse/react';

import useBrowseContentfulFaqs from 'bundles/browse-common/hooks/useBrowseContentfulFaqs';
import Markdown from 'bundles/cms/components/Markdown';
import { usePageConfigComponentMetadata } from 'bundles/page-config-common/contexts/PageConfigComponentMetadataContext';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import HelpCenterInfo from 'bundles/unified-common/components/HelpCenterInfo';
import Section from 'bundles/unified-description-page-common/components/shared/Section';

import _t from 'i18n!nls/browse-common';

const LoadableFAQsModule = createLoadableComponent(
  () => import('@coursera-web/cds-labs/components/Modules/FAQsModule')
);

const BrowseFAQsModuleRenderer = () => {
  const isEQPPage = useIsEqp();
  const pageConfigComponentMetadataContext = usePageConfigComponentMetadata();
  const pageConfigComponentIndex = pageConfigComponentMetadataContext?.index;
  const pageConfigComponentName = pageConfigComponentMetadataContext?.name;

  const { loading, error, pageContent } = useBrowseContentfulFaqs({
    isEntityPage: isEQPPage,
  });

  const { name } = pageContent || {};

  const getHeadline = () => {
    if (name) {
      return _t('Frequently asked questions about #{domainName} courses', {
        domainName: name,
      });
    } else {
      return _t('Frequently asked questions');
    }
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_config_component',
    {
      pageConfigComponent: {
        index: pageConfigComponentIndex ?? 0,
        name: pageConfigComponentName || '',
      },
    }
  );

  if (error || (!loading && (!pageContent?.faqs || pageContent.faqs.length === 0))) {
    return null;
  }
  const markdownFaqs =
    !loading && pageContent?.faqs
      ? pageContent.faqs.map((faq) => {
          return {
            question: faq.question,
            plainTextAnswer: faq.answer,
            answer: <Markdown source={faq.answer} />,
          };
        })
      : [];

  return (
    <Section ref={visibilityTrackingRef} spacing={0}>
      <LoadableFAQsModule
        headline={getHeadline()}
        headlineTypographyVariant="subtitleLarge"
        faqs={markdownFaqs}
        moreInfo={<HelpCenterInfo />}
      />
    </Section>
  );
};

export default BrowseFAQsModuleRenderer;
